import type { Exhibitor, Session, SpecialEvent, Talent, TenantBundle } from './types';
import { findDay, sessionMeta } from './selectors';

/**
 * Ask — natural-language questions about the event.
 *
 * Same contract as the Call Sheet, for the same reason: the model writes the
 * answer, the app owns every fact that can send someone to the wrong place. The
 * model returns prose plus a list of entity references; the app validates those
 * references against the bundle and renders real cards beneath the answer. A
 * reference to something that does not exist is silently dropped.
 *
 * The prose itself is the one thing we cannot validate, which is why the model
 * is told to answer only from the supplied index and to say when it does not
 * know — and why the UI carries a short "check the schedule" line.
 */

export type RefKind = 'session' | 'talent' | 'exhibitor' | 'event';

export interface AnswerRef {
  kind: RefKind;
  id: string;
}

export interface ResolvedRef {
  kind: RefKind;
  id: string;
  title: string;
  meta: string;
  imageUrl?: string;
}

export interface AskResult {
  question: string;
  answer: string;
  refs: ResolvedRef[];
  /** True when this came from the offline keyword search rather than the model. */
  fallback: boolean;
  /** Distinguishes "no signal" from "throttled" from "server has no key". */
  reason?: AskReason;
  /** Seconds from a 429's Retry-After, when the server sent one. */
  retryAfter?: number;
  /** HTTP status, when there was one. */
  status?: number;
  usage?: { in: number; out: number };
}

export interface AskRequest {
  tenantId: string;
  question: string;
  /** Used only to break ties in what to surface first, never to filter answers. */
  interests: string[];
}

/**
 * The compact index handed to the model.
 *
 * Nocturne is small enough — 19 sessions, 8 guests, 12 exhibitors — to send
 * whole. A 600-session event will not be: at that size this needs to become a
 * retrieval step that selects a relevant slice first. The index shape stays the
 * same either way, so only the selection changes.
 */
export function buildEventIndex(bundle: TenantBundle): string {
  return JSON.stringify(buildEventIndexObject(bundle), null, 0);
}

/** The index as a plain object, for endpoints that embed it themselves. */
export function buildEventIndexObject(bundle: TenantBundle): Record<string, unknown> {
  const sessions = bundle.sessions.map((s) => ({
    id: s.id,
    day: findDay(bundle, s.day).label,
    time: `${s.t}-${s.end}`,
    title: s.title,
    room: s.room,
    track: s.track,
    with: s.who.map((id) => bundle.talent.find((t) => t.id === id)?.name ?? id),
    tags: s.tags,
  }));

  const talent = bundle.talent.map((t) => ({
    id: t.id,
    name: t.name,
    role: t.role,
    days: t.days,
    known_for: t.fandoms,
    prices: t.prices,
  }));

  const exhibitors = bundle.exhibitors.map((x) => ({
    id: x.booth,
    name: x.name,
    booth: x.booth,
    zone: x.zone,
    sells: x.offers,
    exclusive: x.exclusive || undefined,
    note: x.note,
  }));

  const events = bundle.specialEvents.map((e) => ({
    id: e.id,
    title: e.title,
    when: e.when,
    room: e.room,
    price: e.price,
    status: e.status,
  }));

  return {
      event: bundle.event.name,
      venue: bundle.event.venueName,
      dates: bundle.event.dateRange,
      currency: bundle.event.currency,
      days: bundle.days.map((d) => ({ key: d.key, label: d.label, hours: d.note })),
      zones: Object.entries(bundle.venue.zones).map(([k, v]) => ({ id: k, label: v.label })),
      sessions,
      talent,
      exhibitors,
      special_events: events,
  };
}

export const ASK_SYSTEM = `You answer attendees' questions about a fan convention, using only the event data you are given.

Rules:
- Answer only from the supplied data. If the answer is not in there, say so plainly and suggest where they could ask instead (the info desk). Never guess a time, room, price or name.
- Be brief. Two or three sentences. These are people standing in a corridor holding a phone.
- Refer to things by name, not by id.
- Prices are in the currency given.
- If a question is about something the event does not have, say that directly rather than offering the nearest alternative as if it were the answer.

Respond with JSON only. No preamble, no markdown fences.

{
  "answer": "your answer in plain prose",
  "refs": [ { "kind": "session|talent|exhibitor|event", "id": "exact id from the data" } ]
}

Include up to 4 refs for the things you mentioned, so the app can show cards for them. Use exact ids. If nothing specific is referenced, use an empty array.`;

export function buildAskPrompt(bundle: TenantBundle, question: string): string {
  return [
    `Event data:`,
    buildEventIndex(bundle),
    '',
    `Attendee question: ${question}`,
  ].join('\n');
}

/** Resolves a model-supplied reference to real bundle content, or null. */
export function resolveRef(bundle: TenantBundle, ref: AnswerRef): ResolvedRef | null {
  if (ref.kind === 'session') {
    const s: Session | undefined = bundle.sessions.find((x) => x.id === ref.id);
    if (!s) return null;
    return {
      kind: 'session',
      id: s.id,
      title: s.title,
      meta: sessionMeta(bundle, s),
      imageUrl: s.imageUrl ?? bundle.talent.find((t) => t.id === s.who[0])?.photoUrl,
    };
  }
  if (ref.kind === 'talent') {
    const t: Talent | undefined = bundle.talent.find((x) => x.id === ref.id);
    if (!t) return null;
    return { kind: 'talent', id: t.id, title: t.name, meta: t.role, imageUrl: t.photoUrl };
  }
  if (ref.kind === 'exhibitor') {
    const x: Exhibitor | undefined = bundle.exhibitors.find(
      (e) => e.booth === ref.id || e.name === ref.id,
    );
    if (!x) return null;
    return { kind: 'exhibitor', id: x.booth, title: x.name, meta: `Booth ${x.booth} · ${x.note}` };
  }
  const e: SpecialEvent | undefined = bundle.specialEvents.find((x) => x.id === ref.id);
  if (!e) return null;
  return { kind: 'event', id: e.id, title: e.title, meta: `${e.when} · ${e.room} · ${e.price}` };
}

export function parseAsk(bundle: TenantBundle, question: string, raw: string): AskResult | null {
  const cleaned = raw.trim().replace(/^```(?:json)?/i, '').replace(/```$/, '').trim();

  let parsed: unknown;
  try {
    parsed = JSON.parse(cleaned);
  } catch {
    return null;
  }

  const obj = parsed as { answer?: unknown; refs?: unknown };
  if (typeof obj.answer !== 'string' || !obj.answer.trim()) return null;

  const refs: ResolvedRef[] = [];
  if (Array.isArray(obj.refs)) {
    for (const entry of obj.refs) {
      const r = entry as { kind?: unknown; id?: unknown };
      if (typeof r.kind !== 'string' || typeof r.id !== 'string') continue;
      if (!['session', 'talent', 'exhibitor', 'event'].includes(r.kind)) continue;
      const resolved = resolveRef(bundle, { kind: r.kind as RefKind, id: r.id });
      if (!resolved) continue;                                        // invented id
      if (refs.some((x) => x.kind === resolved.kind && x.id === resolved.id)) continue;
      refs.push(resolved);
      if (refs.length >= 4) break;
    }
  }

  return { question, answer: obj.answer.trim().slice(0, 800), refs, fallback: false, reason: 'ok' };
}

/**
 * Offline keyword search. Not an answer — a list of things that match the words
 * the attendee typed. Used whenever the model is unreachable, which at a
 * convention venue is a normal Saturday afternoon.
 */
export function localAsk(bundle: TenantBundle, question: string): AskResult {
  const terms = question
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length > 2);

  const score = (haystack: string[]) => {
    const text = haystack.join(' ').toLowerCase();
    return terms.reduce((n, term) => n + (text.includes(term) ? 1 : 0), 0);
  };

  const candidates: { ref: ResolvedRef; score: number }[] = [];

  for (const s of bundle.sessions) {
    const n = score([s.title, s.room, s.track, ...(s.tags ?? []), s.desc ?? '',
      ...s.who.map((id) => bundle.talent.find((t) => t.id === id)?.name ?? '')]);
    if (n) {
      const ref = resolveRef(bundle, { kind: 'session', id: s.id });
      if (ref) candidates.push({ ref, score: n });
    }
  }
  for (const t of bundle.talent) {
    const n = score([t.name, t.role, t.bio, ...t.fandoms]);
    if (n) {
      const ref = resolveRef(bundle, { kind: 'talent', id: t.id });
      if (ref) candidates.push({ ref, score: n });
    }
  }
  for (const x of bundle.exhibitors) {
    const n = score([x.name, x.note, x.booth, ...x.offers, x.exclusive]);
    if (n) {
      const ref = resolveRef(bundle, { kind: 'exhibitor', id: x.booth });
      if (ref) candidates.push({ ref, score: n });
    }
  }
  for (const e of bundle.specialEvents) {
    const n = score([e.title, e.desc, e.room]);
    if (n) {
      const ref = resolveRef(bundle, { kind: 'event', id: e.id });
      if (ref) candidates.push({ ref, score: n });
    }
  }

  candidates.sort((a, b) => b.score - a.score);
  const refs = candidates.slice(0, 6).map((c) => c.ref);

  return {
    question,
    answer: refs.length
      ? `${refs.length} match${refs.length === 1 ? '' : 'es'} for “${question.trim()}”.`
      : `Nothing in the programme matches “${question.trim()}”.`,
    refs,
    fallback: true,
    reason: 'offline',
  };
}

/**
 * Where the AI endpoint lives.
 *
 * `baseUrl` is the origin only — the path is fixed at /api/ai/ask. `token` is
 * sent as a bearer when the server has MONITOR_TOKEN set; leave it undefined
 * for an open server.
 */
export interface AiConfig {
  baseUrl: string;
  token?: string;
}

export interface AskTurn {
  role: 'user' | 'assistant';
  content: string;
}

/** Why an answer is what it is — the UI says something different for each. */
export type AskReason =
  | 'ok'
  /** No endpoint configured in the client build — no request was attempted. */
  | 'no-endpoint'
  /** Request was attempted and failed: network, CORS, 4xx, 5xx, bad format. */
  | 'offline'
  | 'rate-limited'
  /** Server reachable but has no API key (503). */
  | 'unconfigured'
  /** Server answered 200 but not in the {answer, refs} shape. */
  | 'bad-format';

/**
 * The response-shape contract, restated in the user turn.
 *
 * The server owns its own system prompt and embeds the snapshot there, so we
 * cannot assume it asks for our JSON shape. Repeating the contract here costs
 * ~120 tokens and makes the client independent of a server prompt that may
 * change without us. `parseAsk` still validates, and still falls back if the
 * model ignores it.
 */
const FORMAT_CONTRACT = `

Answer using only the event data in your context. Never guess a time, room, price or name; if it is not in the data, say so and point them at the info desk. Two or three sentences.

Reply with JSON only — no prose outside it, no markdown fences:
{"answer":"...","refs":[{"kind":"session|talent|exhibitor|event","id":"exact id from the data"}]}
Up to 4 refs, exact ids only, empty array if nothing specific is referenced.`;

/**
 * Asks the AI endpoint. Resolves to the offline result on any failure — never
 * rejects, so callers have one code path.
 *
 * The client builds the index and sends it as `snapshot`; the server embeds
 * whatever it is given. Sent as an object rather than a pre-stringified field
 * so it is not double-encoded inside the prompt.
 */
export async function fetchAnswer(
  config: AiConfig | string,
  bundle: TenantBundle,
  request: AskRequest,
  fetchImpl: typeof fetch = fetch,
  signal?: AbortSignal,
  history: AskTurn[] = [],
): Promise<AskResult> {
  const cfg: AiConfig = typeof config === 'string' ? { baseUrl: config } : config;

  // Distinct from 'offline': nothing was even attempted. Reported separately
  // because a missing build-time variable and a failed request look identical
  // on screen otherwise, and the first one leaves no trace in the Network tab.
  if (!cfg.baseUrl) {
    console.warn(
      '[ask] No AI endpoint configured — set VITE_AI_URL (web) or EXPO_PUBLIC_AI_URL (native) ' +
        'before building. Falling back to offline keyword search.',
    );
    return { ...localAsk(bundle, request.question), reason: 'no-endpoint' };
  }

  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (cfg.token) headers.authorization = `Bearer ${cfg.token}`;

  // The endpoint clamps to 12 turns and 4000 chars each; stay inside that here
  // so nothing is silently truncated server-side.
  const messages: AskTurn[] = [
    ...history.slice(-10).map((m) => ({ role: m.role, content: m.content.slice(0, 4000) })),
    { role: 'user' as const, content: `${request.question}${FORMAT_CONTRACT}` },
  ];

  try {
    const res = await fetchImpl(`${cfg.baseUrl.replace(/\/$/, '')}/api/ai/ask`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        messages,
        snapshot: buildEventIndexObject(bundle),
      }),
      signal,
    });

    if (res.status === 429) {
      const retry = Number(res.headers.get('retry-after')) || 0;
      return { ...localAsk(bundle, request.question), reason: 'rate-limited', retryAfter: retry };
    }
    if (res.status === 503) {
      return { ...localAsk(bundle, request.question), reason: 'unconfigured' };
    }
    if (!res.ok) {
      console.warn(`[ask] ${cfg.baseUrl}/api/ai/ask returned ${res.status}`);
      return { ...localAsk(bundle, request.question), reason: 'offline', status: res.status };
    }

    const data = (await res.json()) as { reply?: string; usage?: { in: number; out: number } };
    if (typeof data.reply !== 'string') {
      return { ...localAsk(bundle, request.question), reason: 'offline' };
    }

    const parsed = parseAsk(bundle, request.question, data.reply);
    if (!parsed) {
      // The server answered, but not in the {answer, refs} shape. Worth naming:
      // everything looks healthy and the feature silently degrades.
      console.warn('[ask] Reply was not in the expected JSON shape:', data.reply?.slice(0, 200));
      return { ...localAsk(bundle, request.question), reason: 'bad-format' as AskReason };
    }
    return { ...parsed, usage: data.usage };
  } catch (err) {
    // Fires for network failure, CORS rejection and mixed-content blocking
    // alike — the browser deliberately does not tell us which.
    if ((err as Error)?.name !== 'AbortError') {
      console.warn(`[ask] Request to ${cfg.baseUrl} failed (network, CORS or mixed content):`, err);
    }
    return { ...localAsk(bundle, request.question), reason: 'offline' };
  }
}

/**
 * Suggested questions.
 *
 * Built locally from the attendee's interests and the day's content rather than
 * by a model call. Suggestions appear the instant the search screen opens, on
 * every launch, for every attendee — paying for a model call to render a prompt
 * chip would be the single most expensive thing in the app.
 */
export function suggestQuestions(
  bundle: TenantBundle,
  interests: string[],
  day: string,
): string[] {
  const out: string[] = [];
  const cur = bundle.event.currency;

  const matchingTalent = bundle.talent.filter((t) => t.fandoms.some((f) => interests.includes(f)));
  const guest = matchingTalent[0] ?? bundle.talent[0];
  if (guest) {
    out.push(`When is ${guest.name.split(' ')[0]} signing?`);
    out.push(`How much is a photo op with ${guest.name}?`);
  }

  const dayLabel = findDay(bundle, day).label.split(' ')[0];
  out.push(`What should I do ${dayLabel} morning?`);

  const interest = interests[0];
  if (interest) out.push(`Any ${interest.toLowerCase()} panels left?`);

  const exclusive = bundle.exhibitors.find((x) => x.exclusive);
  if (exclusive) out.push(`Which booths have con exclusives?`);

  const ticketed = bundle.specialEvents.find((e) => e.price.startsWith(cur));
  if (ticketed) out.push(`Is ${ticketed.title} sold out?`);

  out.push('Where do I collect photo op prints?');
  out.push('What is there for kids?');

  // Deduplicate while preserving the interest-led ordering.
  return [...new Set(out)].slice(0, 6);
}
